import { useState } from 'react';
import {
  Radio,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItemAvatar,
} from '@mui/material';
import { dialogOverlay, StyledDialogActions, SuccessActionButton } from '../styles';
import { primary } from '@/theme/colors';
import { GenericModal } from '@/components/shared/GenericModal';
import { idmsTemplates } from '../data';
import { hexColorWithAlpha } from '@/utils';
import {
  IdmsTasksDocument,
  IdmsUploadsDocument,
  IdmsUploadsTypeEnum,
  useIdmsFileUploadMutation,
  useValidateIdmsFileLazyQuery,
} from '@/__generated__/graphql';
import { csvStringToJson } from '../utils';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { ValidateFileModal } from './ValidateFileModal';
import { CsvParsedData, CsvValidationIssue } from '../types';
import { ConfirmationDialog } from '@/components/shared/ConfirmationDialog';
import { FileExceptionModal } from './FileExceptionModal';
import ExcelJS from 'exceljs';

type Props = {
  file: File | undefined;
  open: boolean;
  handleClose: () => void;
  title?: string;
  description?: string;
  fileInputReference: React.MutableRefObject<HTMLInputElement | undefined>;
};

export const FileTypeModal = ({
  file,
  open,
  handleClose,
  title,
  description,
  fileInputReference,
}: Props) => {
  const [selectedFileType, setSelectedFileType] = useState<IdmsUploadsTypeEnum | undefined>(
    undefined
  );
  const [uploadedCsvParsedData, setUploadedCsvParsedData] = useState<CsvParsedData>(undefined);
  const [idmsValidationErrors, setIdmsValidationErrors] = useState<CsvValidationIssue | undefined>(
    undefined
  );
  const [exceptionsModal, setFileExceptionModal] = useState(false);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [idmsUploads, { loading: idmsUploadLoading }] = useIdmsFileUploadMutation();
  const [isValidating, setIsValidating] = useState(false);
  const [validateFile] = useValidateIdmsFileLazyQuery();
  const structureId = useGetStructureId();

  const handleFileUpload = () => {
    if (!file) return;

    // Check if file is XLSX or CSV based on extension
    const isXlsx = file.name.toLowerCase().endsWith('.xlsx');

    if (isXlsx) {
      handleXlsxFileUpload();
    } else {
      handleCsvFileUpload();
    }
  };

  const handleCsvFileUpload = () => {
    const reader = new FileReader();

    reader.addEventListener('load', (readEvent) => {
      const text = readEvent.target?.result?.toString();

      if (!text) return;

      const { header, rows } = csvStringToJson(text);
      setUploadedCsvParsedData({ header, rows });
      validateFileContent(text);
    });

    if (file) reader.readAsText(file);
  };

  const handleXlsxFileUpload = () => {
    const reader = new FileReader();

    reader.addEventListener('load', async (readEvent) => {
      try {
        if (!readEvent.target?.result) return;

        const arrayBuffer = readEvent.target.result as ArrayBuffer;
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(arrayBuffer);

        const worksheet = workbook.worksheets[0];
        if (!worksheet) {
          throw new Error('No worksheet found in the Excel file');
        }

        // Extract headers (first row)
        const header: string[] = [];
        worksheet.getRow(1).eachCell((cell) => {
          header.push(cell.value?.toString() || '');
        });

        // Extract data rows
        const rows: Record<string, unknown>[] = [];
        worksheet.eachRow((row, rowNumber) => {
          if (rowNumber === 1) return; // Skip header row

          const rowData: Record<string, unknown> = {};
          row.eachCell((cell, colNumber) => {
            if (colNumber <= header.length) {
              rowData[header[colNumber - 1]] = cell.value?.toString() || '';
            }
          });

          rows.push(rowData);
        });

        // Convert Excel data to CSV format for validation
        const csvString = convertToCsvString(header, rows);

        setUploadedCsvParsedData({ header, rows });
        validateFileContent(csvString);
      } catch (error) {
        console.error('Error processing XLSX file:', error);
        setIdmsValidationErrors({
          type: 'validation',
          error: [{
            line: 0,
            errorMap: {
              XLSX_ERROR: [{
                message: 'Error processing XLSX file',
                code: 'XLSX_ERROR'
              }]
            }
          }]
        });
        setFileExceptionModal(true);
        setIsValidating(false);
      }
    });

    reader.addEventListener('error', () => {
      setIdmsValidationErrors({
        type: 'validation',
        error: [{
          line: 0,
          errorMap: {
            FILE_READ_ERROR: [{
              message: 'Error reading the file',
              code: 'FILE_READ_ERROR'
            }]
          }
        }]
      });
      setFileExceptionModal(true);
      setIsValidating(false);
    });

    if (file) reader.readAsArrayBuffer(file);
  };

  const convertToCsvString = (header: string[], rows: Record<string, unknown>[]): string => {
    const headerLine = header.join(',');
    const dataLines = rows.map(row => {
      return header.map(key => row[key] || '').join(',');
    });

    return [headerLine, ...dataLines].join('\n');
  };

  const validateFileContent = (csvString: string) => {
    setIsValidating(true);
    if (!selectedFileType || !structureId) return;

    validateFile({
      variables: {
        input: {
          csvString,
          template: selectedFileType,
          structureId,
        },
      },
    })
      .then((data) => {
        if (data?.data?.validateIdmsTemplate) {
          setIdmsValidationErrors(undefined);
          setIsValidating(false);
          setOpenConfirmationDialog(true);
          return;
        }
        const issue: CsvValidationIssue | undefined = (() => {
          const exception = data.error?.graphQLErrors?.[0]?.extensions?.exception;
          if (exception && typeof exception === 'object' && 'issue' in exception) {
            return exception.issue as CsvValidationIssue;
          }
          return undefined;
        })();
        setIdmsValidationErrors(issue);
        setFileExceptionModal(true);
        setIsValidating(false);
      })
      .catch((error) => {
        const issue: CsvValidationIssue | undefined = (() => {
          const exception = error?.graphQLErrors?.[0]?.extensions?.exception;
          if (exception && typeof exception === 'object' && 'issue' in exception) {
            return exception.issue as CsvValidationIssue;
          }
          return undefined;
        })();
        setIdmsValidationErrors(issue);
        setFileExceptionModal(true);
        setIsValidating(false);
      });
  };

  const handleIdmsFileUpload = (validRecordsFile?: Blob) => {
    if (!file) return;

    const reader = new FileReader();

    reader.addEventListener('load', () => {
      const base64File = reader.result?.toString().split(',')[1];
      if (base64File && selectedFileType) {
        idmsUploads({
          variables: {
            input: {
              name: file.name,
              structureId,
              file: base64File,
              templateType: selectedFileType,
            },
          },
          refetchQueries: [IdmsUploadsDocument, IdmsTasksDocument],
        }).then(() => {
          setSelectedFileType(undefined);
          setFileExceptionModal(false);
          setOpenConfirmationDialog(false);
        });
      }
    });

    if (validRecordsFile) reader.readAsDataURL(validRecordsFile);
    else if (file) reader.readAsDataURL(file);
  };

  const handleCancelUpload = () => {
    setSelectedFileType(undefined);
    setOpenConfirmationDialog(false);
  };

  const handleFileValidation = () => {
    handleFileUpload();
    handleClose();
  };

  const handleFileTypeModalClose = () => {
    setSelectedFileType(undefined);
    handleClose();
  };

  const handleCloseFileExceptionModal = () => {
    setFileExceptionModal(false);
    setSelectedFileType(undefined);
  };

  const excludedNames = new Set(['APM Line Metadata', 'AF Inspection Priority']);

  return (
    <>
      <GenericModal
        open={open}
        handleClose={handleClose}
        title={title}
        description={description}
        crossIconStyle={{ backgroundColor: 'transparent', color: 'primary' }}
        sx={{ ...dialogOverlay, left: '37% !important', width: 536, minHeight: 572 }}
      >
        <List sx={{ maxHeight: 392, overflowY: 'auto' }}>
          {idmsTemplates
            .filter(({ name }) => !excludedNames.has(name))
            .map(({ name, type }) => (
              <ListItem
                data-testid="file-type-list-item"
                disableGutters
                key={type}
                sx={{
                  py: 0,
                  borderTop: 1,
                  borderColor: 'divider',
                  '&:hover': { backgroundColor: hexColorWithAlpha(primary.light, 20) },
                }}
              >
                <ListItemButton
                  sx={{ '&:hover': { background: 'none' } }}
                  onClick={() => setSelectedFileType(type)}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Radio checked={selectedFileType === type} />
                  </ListItemIcon>
                  <ListItemAvatar sx={{ minWidth: '30px', pl: 1, pr: 2 }}>
                    <img
                      src="/assets/icons/file-icon.svg"
                      alt="File_Preview"
                      width={30}
                      height={40}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primaryTypographyProps={{ fontSize: '1.4rem', fontWeight: '600' }}
                    primary={name}
                  />
                </ListItemButton>
              </ListItem>
            ))}
        </List>
        <StyledDialogActions>
          <Button onClick={handleFileTypeModalClose} variant="text" sx={{ mr: 1, height: '36px' }}>
            Cancel
          </Button>
          <SuccessActionButton
            variant="contained"
            onClick={handleFileValidation}
            disabled={!selectedFileType}
          >
            Confirm
          </SuccessActionButton>
        </StyledDialogActions>
      </GenericModal>

      <ValidateFileModal open={isValidating} />

      <ConfirmationDialog
        title="Success"
        description="The file has been uploaded and validated successfully. Kindly confirm to proceed with importing the data into our system"
        confirmButtonText="Confirm"
        cancelButtonText="Cancel"
        isOpen={openConfirmationDialog}
        onConfirm={handleIdmsFileUpload}
        onCancel={handleCancelUpload}
        handleClose={() => {}}
        loading={{
          state: idmsUploadLoading,
          text: 'Uploading...',
        }}
      />

      <FileExceptionModal
        open={exceptionsModal}
        idmsUploadLoading={idmsUploadLoading}
        selectedFileType={selectedFileType}
        handleIdmsFileUpload={handleIdmsFileUpload}
        handleClose={handleCloseFileExceptionModal}
        uploadedCsvParsedData={uploadedCsvParsedData}
        idmsValidationErrors={idmsValidationErrors}
        fileInputReference={fileInputReference}
      />
    </>
  );
};
